import React, {
  useEffect,
  useState,
  useRef,
  useContext,
  createContext,
} from 'react'
import { apiPost, apiDelete, apiGet } from '../../../util/api'
import useResourcesForm from './useResourcesForm'
import { useResourcesParams } from './useResourcesParams'
import { useSearchQuery } from '../../../hooks/useSearchQuery'

const IndustryResourcesContext = createContext()

export function useIndustryResources() {
  return useContext(IndustryResourcesContext)
}

export function IndustryResourcesProvider({
  search_results,
  total_results,
  total_pages,
  next_page,
  allow_debug,
  saved_resources,
  children,
}) {
  const [selectedResourceDrawerOpen, setSelectedResourceDrawerOpen] =
    useState(false)
  const [selectedResource, setSelectedResource] = useState(null)
  const [noResultQuery, setNoResultQuery] = useState('')
  const [viewMoreButtonDisabled, setViewMoreButtonDisabled] = useState(false)
  const [formDisabled, setFormDisabled] = useState(false)
  const [savedResources, setSavedResources] = useState(saved_resources || [])
  const [previousFirstResourceId, setPreviousFirstResourceId] = useState(null)

  const resultDetailScrollRef = useRef(null)

  const { urlSearchParams, setUrlSearchParams, paramString, lockParamString } =
    useResourcesParams(location)
  const debug = allow_debug && urlSearchParams.has('debug')
  const {
    queryStatusMessage,
    pagesToShow,
    setPagesToShow,
    setPrefetchedPages,
    results,
    amountShown,
    amountTotal,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isPending,
    showViewMoreButton,
  } = useSearchQuery({
    queryKey: 'industry-resources-search',
    paramString,
    initialData: {
      pageParams: [1],
      pages: [
        {
          results: search_results,
          total_results: total_results,
          total_pages: total_pages,
          next_page: next_page,
        },
      ],
    },
  })

  const { form, onSubmit } = useResourcesForm({
    urlSearchParams,
    lockParamString,
    noResultQuery,
    setPagesToShow,
    setPrefetchedPages,
  })

  const { watch } = form
  const type = watch('type')
  const industry = watch('industry')
  const keywords = watch('keywords')

  useEffect(() => {
    setUrlSearchParams(
      (prev) => {
        if (type) prev.set('type', type)
        else prev.delete('type')

        if (industry) prev.set('industry', industry)
        else prev.delete('industry')

        if (keywords) prev.set('keywords', keywords)
        else prev.delete('keywords')
        return prev
      },
      { replace: true },
    )
  }, [type, industry, keywords])

  const hasFilters = !!(type || industry || keywords)
  const resetFilters = () => {
    form.setValue('type', 0)
    form.setValue('industry', undefined)
    form.setValue('keywords', '')
  }

  useEffect(() => {
    setFormDisabled(isFetching && pagesToShow === 1 && !isFetchingNextPage)
  }, [pagesToShow, isFetching, isFetchingNextPage, isPending])

  useEffect(() => {
    const firstResource = results[0]
    if (firstResource && firstResource.id !== previousFirstResourceId) {
      setPreviousFirstResourceId(firstResource.id)
      setSelectedResource(firstResource)
    }
  }, [results, previousFirstResourceId])

  useEffect(() => {
    setNoResultQuery(results.length === 0)
  }, [results.length, paramString])

  const handleSelectResource = (resource) => (event) => {
    event.stopPropagation()
    resultDetailScrollRef.current?.scrollTo(0, 0, { behavior: 'smooth' })
    setSelectedResource(null)
    setSelectedResource(resource)
    setSelectedResourceDrawerOpen(true)
  }

  const handleViewMore = async () => {
    setViewMoreButtonDisabled(true)
    setPagesToShow((prev) => prev + 1)
    await fetchNextPage()
    setViewMoreButtonDisabled(false)
  }

  const savedResourceIds = savedResources.map((resource) => {
    const externalIdParts = resource.external_id.split('_')
    return parseInt(externalIdParts[1], 10) // Convert to an integer
  })

  const isSavedResource = (resource) => {
    return savedResourceIds.includes(resource.id)
  }

  const resourceExternalIds = savedResources.map((resource) => {
    return resource.external_id
  })

  const isSavedContact = (contract_external_id) => {
    return resourceExternalIds.includes(contract_external_id)
  }

  const ToConnectionsPath = '/networking/my_connections/add_to_connections'

  const fetchSavedResources = async () => {
    try {
      const response = await apiGet(
        `/networking/my_connections/fetch_bir_connections`,
      )
      if (response.status === 200) {
        const data = await response.data
        console.log('Fetched saved resources:', data.saved_resources)
        setSavedResources(data.saved_resources)
      } else {
        throw new Error('Failed to fetch saved resources')
      }
    } catch (error) {
      console.error('Error fetching saved resources:', error)
    }
  }

  const addToConnections = async (resource, contact) => {
    const contactKey = contact?.contact_key || 'contact_0'
    const externalId = `BIR_${resource.id}_${contactKey}`
    try {
      const response = await apiPost(ToConnectionsPath, {
        name: contact?.full_name || '',
        title: contact?.title || '',
        company: resource.name,
        phone: resource.phone,
        email: resource.email,
        location: resource.city_state,
        external_id: externalId,
      })
      if (response.status === 201) {
        console.log('Resource added successfully:', await response.data)
        await fetchSavedResources(resource, contact)
      } else {
        throw new Error('Failed to add resource')
      }
    } catch (error) {
      console.error('Error adding resource:', error)
    }
  }

  const removeFromMyConnections = async (resource, contact) => {
    const externalId = `BIR_${resource.id}_${contact.contact_key}`
    const removeFromMyConnectionsPath = `/networking/my_connections/remove_from_connections?external_id=${externalId}`

    try {
      const response = await apiDelete(removeFromMyConnectionsPath)
      if (response.status === 200) {
        console.log('Resource removed successfully:', await response.data)
        await fetchSavedResources(resource, contact)
      } else {
        throw new Error('Failed to remove resource')
      }
    } catch (error) {
      console.error('Error removing resource:', error)
    }
  }

  const value = {
    form,
    formDisabled,
    onSubmit,
    queryStatusMessage,
    pagesToShow,
    setPagesToShow,
    setPrefetchedPages,
    results,
    amountShown,
    amountTotal,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isPending,
    showViewMoreButton,
    viewMoreButtonDisabled,
    resultDetailScrollRef,
    selectedResourceDrawerOpen,
    setSelectedResourceDrawerOpen,
    selectedResource,
    setSelectedResource,
    noResultQuery,
    setNoResultQuery,
    handleSelectResource,
    handleViewMore,
    isSavedResource,
    isSavedContact,
    addToConnections,
    removeFromMyConnections,
    debug,
    hasFilters,
    resetFilters,
  }

  return (
    <IndustryResourcesContext.Provider value={value}>
      {children}
    </IndustryResourcesContext.Provider>
  )
}
